import { useQuery } from 'react-query'

import settings from '../app/components/settings'
import { user } from './initValues'

const POSTCODE_ITEM_TYPE = 'Postcode'
const ADDRESS_ITEM_TYPE = 'Address'

/**
 * Retrieves additional address data for a particular address.
 * @param {string} refinementId ID of an address type search response.
 * @returns {Promise<{
 *  id: string
 *  city: string
 *  line1: string
 *  line2: string
 *  line3: string
 *  postalCode: string
 *  countryIso2: string
 *  countryName: string
 *  label: string
 * }>}
 */
const retrieveAddress = async (refinementId) => {
  const url = `${settings.badaAppointmentApiUrl}/location/retrieve-address?id=${refinementId}`
  const response = await fetch(url).then((r) =>
    r.status === 200 ? r.json() : undefined
  )

  if (!response) {
    throw new Error(`No address could be retrieved for ID '${refinementId}'!`)
  }
  if (response.id) {
    return response
  }

  return {
    id: response.Id,
    city: response.City,
    line1: response.Line1,
    line2: response.Line2,
    line3: response.Line3,
    postalCode: response.PostalCode,
    countryIso2: response.CountryIso2,
    countryName: response.CountryName,
    label: response.Label,
    buildingNumber: response.BuildingNumber,
    buildingName: response.BuildingName,
    street: response.Street,
    company: response.Company,
  }
}

/**
 * Searches for address data matching given query.
 * @param {string} query Address search query.
 * @param {string?} refinementId ID of a non-address type search response.
 * @param {number} limit Max number of search results.
 * @param {string[]} countries Lists of countries to limit the search within.
 * @param {string} filterPostcode If the search should be limited to a specific postalcode.
 * @returns {Promise<{
 *  id: string,
 *  type: string,
 *  text: string,
 *  highlight: string,
 *  description: string
 * }[]>}
 */
const searchAddresses = async ({
  searchText,
  refinementId,
  limit,
  filterPostcode,
}) => {
  if (!searchText && !refinementId) {
    return []
  }
  const url = new URL(
    `${settings.badaAppointmentApiUrl}/location/search-address`
  )
  url.searchParams.append('brand', settings.brand)

  if (searchText) {
    url.searchParams.append('searchText', searchText)
  }
  if (limit) {
    url.searchParams.append('limit', limit)
  }
  if (refinementId) {
    url.searchParams.append('refinementId', refinementId)
  }

  if (filterPostcode) {
    url.searchParams.append('filterPostcode', filterPostcode)
  }
  const response = await fetch(url.toString()).then((r) =>
    r.status === 200 ? r.json() : { Items: [], addresses: [] }
  )

  if (response.addresses) {
    return response.addresses
  }

  return response.Items.map((i) => ({
    id: i.Id,
    type: i.Type,
    text: i.Text,
    highlight: i.Highlight,
    description: i.Description,
  }))
}

const geocodeAddress = async (address) => {
  if (!address) {
    return {}
  }
  const url = new URL(
    `${settings.badaAppointmentApiUrl}/location/search-geocode`
  )
  url.searchParams.append('address', address)
  url.searchParams.append('address', settings.countryCode)
  url.searchParams.append('brand', settings.brand)

  const response = await fetch(url.toString()).then((r) =>
    r.status === 200 ? r.json() : {}
  )
  return {
    latitude: response.latitude,
    longitude: response.longitude,
    city: response.address?.address_components?.at(1)?.long_name,
  }
}

const serviceProviderByEmail = async (
  selectedStore,
  selectedServiceProviderEmail
) => {
  const subsystem =
    selectedStore?.simplyBookMeSubsystem == null
      ? settings.defaultSubsystem
      : selectedStore.simplyBookMeSubsystem
  const url = `${settings.badaAppointmentApiUrl}/service-providers/email?brand=${settings.brand}&storeId=${selectedStore.id}&subsystem=${subsystem}`
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email: selectedServiceProviderEmail }),
  }).then((r) => (r.status === 200 ? r.json() : undefined))

  if (!response) {
    throw new Error(
      `Could not get service provider with email '${selectedServiceProviderEmail}'!`
    )
  }

  return response
}

const serviceProviderById = async (
  storeId,
  simplyBookMeSubsystem,
  selectedServiceProviderId
) => {
  const subsystem =
    simplyBookMeSubsystem == null
      ? settings.defaultSubsystem
      : simplyBookMeSubsystem
  const url = `${settings.badaAppointmentApiUrl}/service-providers/id?brand=${settings.brand}&storeId=${storeId}&subsystem=${subsystem}&id=${selectedServiceProviderId}&includeHidden=true`
  const response = await fetch(url).then((r) =>
    r.status === 200 ? r.json() : undefined
  )

  if (!response) {
    throw new Error(
      `Could not get service provider with id '${selectedServiceProviderId}'!`
    )
  }

  return response
}

export const getServiceProviders = async (
  storeId,
  serviceId,
  includeHidden
) => {
  const url = new URL(
    `${settings.badaAppointmentApiUrl}/service-providers/?brand=${
      settings.brand
    }&storeId=${storeId}&includeHidden=${includeHidden || false}`
  )
  if (serviceId) {
    url.searchParams.append('serviceId', serviceId)
  }
  const response = await fetch(url).then((r) =>
    r.status === 200 ? r.json() : undefined
  )

  return response || []
}

const transformStore = (store) =>
  store
    ? {
        ...store,
        id: store.id?.toString(),
        latitude: isNaN(parseFloat(store.latitude, 10))
          ? undefined
          : parseFloat(store.latitude, 10),
        longitude: isNaN(parseFloat(store.longitude, 10))
          ? undefined
          : parseFloat(store.longitude, 10),
      }
    : undefined

export const getStaffMembers = async (storeId) => {
  const url = new URL(`${settings.badaAppointmentApiUrl}/stores/staff`)
  url.searchParams.append('brand', settings.brand)
  url.searchParams.append('storeId', storeId)

  return fetch(url).then((r) => (r.status === 200 ? r.json() : undefined))
}

export const useGetStaffMembers = (storeId) =>
  useQuery(['useGetStaffMembers', storeId], () => getStaffMembers(storeId), {
    enabled: !!storeId,
  })

export const getStores = async (includeFakes) => {
  const url = new URL(`${settings.badaAppointmentApiUrl}/stores/`)
  url.searchParams.append('brand', settings.brand)
  url.searchParams.append('fake', !!includeFakes)

  const response = await fetch(url).then((r) =>
    r.status === 200 ? r.json() : undefined
  )
  if (Array.isArray(response)) {
    return response.map(transformStore)
  }
  return response
}

export const useGetStores = (includeFakes) =>
  useQuery(['useGetStores', !!includeFakes], () => getStores(includeFakes), {})

export const getStore = async (storeId) => {
  const url = new URL(
    `${settings.badaAppointmentApiUrl}/stores/${storeId || '-100'}`
  )
  url.searchParams.append('brand', settings.brand)

  const response = await fetch(url).then((r) =>
    r.status === 200 ? r.json() : undefined
  )

  return transformStore(response)
}

export const useGetStore = (storeId) =>
  useQuery(['useGetStore', storeId], () => getStore(storeId), {
    enabled: !!storeId,
  })

export const getAppointment = (appointmentId) =>
  fetch(
    `${settings.badaAppointmentApiUrl}/appointment/${appointmentId}?brand=${settings.brand}&client=bada`,
    {
      headers: {
        Authorization: user?.access_token
          ? `Bearer ${user.access_token}`
          : undefined,
      },
    }
  )
    .then(async (r) => {
      if (r.ok) {
        return r.json()
      }
      const error = await r.text()
      throw new Error(error.slice(1).slice(0, -1))
    })
    .then((appointment) => ({
      id: appointment.id,
      brand: appointment.brand,
      createdAt: appointment.createdAt,
      client: appointment.client,
      externalMeetingProvider: appointment.externalMeetingProvider,
      serviceProviderId: appointment.simplyBookMeId
        ? parseInt(appointment.serviceProviderId, 10)
        : undefined,
      serviceProviderName: appointment.serviceProviderName,
      serviceId: appointment.simplyBookMeId
        ? parseInt(appointment.serviceId, 10)
        : undefined,
      duration: appointment.duration,
      storeId: appointment.storeId,
      customerName: appointment.customerName,
      customerHouseNumber: appointment.customerHouseNumber,
      customerAddress: appointment.customerAddress,
      customerAddress2: appointment.customerAddress2,
      customerAddress3: appointment.customerAddress3,
      customerPostCode: appointment.customerPostCode,
      customerCity: appointment.customerCity,
      customerCountry: appointment.customerCountry,
      customerEmail: appointment.customerEmail,
      customerPhone: appointment.customerPhone,
      selectedDate: appointment.selectedDate,
      selectedTime: appointment.selectedTime,
      selectedEndTime: appointment.selectedEndTime,
      comment: appointment.comment,
      newsletterConsent: appointment.newsletterConsent,
      source: appointment.source,
      simplyBookMeId: appointment.simplyBookMeId,
      simplyBookMeStatus: appointment.simplyBookMeStatus,
      simplyBookMeSubsystem: appointment.simplyBookMeSubsystem,
      userIdentifier: appointment.userIdentifier,
      externalAppointmentId: appointment.externalAppointmentId,
      externalStoreId: appointment.externalStoreId,
      externalBookingSystem: appointment.externalBookingSystem,
      category: appointment.category,
      categoryId: appointment.categoryId,
      purpose: appointment.purpose,
      clientIp: appointment.clientIp,
      customerVerified: appointment.customerVerified,
      designerSelectedByCustomer: appointment.designerSelectedByCustomer,
      recaptchaScore: appointment.recaptchaScore,
      trustScore: appointment.trustScore,
      appointmentPreparations: {
        comment: appointment.appointmentPreparations?.comment,
        questions: appointment.appointmentPreparations?.questions?.map((q) => ({
          id: q.id,
          question: q.question,
          answers: q.answers,
          answeredAt: q.answeredAt,
        })),

        currentKitchenImagesUrls:
          appointment.appointmentPreparations?.currentKitchenImagesUrls,
        willBringCurrentKitchenImages:
          appointment.appointmentPreparations?.willBringCurrentKitchenImages,
        kitchenScetchImagesUrls:
          appointment.appointmentPreparations?.kitchenScetchImagesUrls,
        willBringKitchenScetchImages:
          appointment.appointmentPreparations?.willBringKitchenScetchImages,
      },
      externalDesignerEmail: appointment.externalDesignerEmail,
      externalDesignerId: appointment.externalDesignerId,
      flow: appointment.flow,
      updated: appointment.updated,
      utmSource: appointment.utmSource,
      utmMedium: appointment.utmMedium,
      cancelledBy: appointment.cancelledBy,
      cancellationReason: appointment.cancellationReason,
    }))

export const useGetAppointment = (appointmentId) =>
  useQuery(
    ['useGetAppointment', appointmentId],
    () => getAppointment(appointmentId),
    {
      enabled: !!appointmentId,
    }
  )

export const createAppointment = ({
  input: {
    service,
    date,
    time,
    email,
    name,
    address,
    address2,
    address3,
    phone,
    postCode,
    city,
    country,
    comments,
    newsletterConsent,
    trackingCookie,
    meetingTypeId,
    userIdentifier,
    bookedByDesigner,
    storeId,
    recaptchaToken,
    affiliateTag,
    transactionId,
    utmSource,
    utmMedium,
    verificationToken,
    serviceProviderId,
    flow,
    questions,
    endTime,
  },
}) =>
  fetch(
    `${settings.badaAppointmentApiUrl}/appointment/kratos/?brand=${settings.brand}&client=bada`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: user?.access_token
          ? `Bearer ${user.access_token}`
          : undefined,
      },
      body: JSON.stringify({
        meetingTypeId,
        serviceCode: service,

        selectedDate: date,
        selectedTime: time,
        selectedEndTime: endTime,
        serviceProviderId,

        storeId: parseInt(storeId, 10),

        trackingCookie,
        flow,

        customerName: name,
        customerHouseName: '',
        customerHouseNumber: '',
        customerAddress: address,
        customerAddress2: address2,
        customerAddress3: address3,
        customerPostCode: postCode,
        customerCity: city,
        customerCountryCode: country,
        customerEmail: email,
        customerPhone: phone,
        comment: comments,
        newsletterConsent,

        bookedByDesigner,
        userIdentifier,

        affiliateTag,
        utmSource,
        utmMedium,
        transactionId,

        recaptchaToken,
        verificationToken,

        questions,
      }),
    }
  )
    .then(async (r) => {
      if (r.ok) {
        return r.json()
      }
      const error = await r.text()
      throw new Error(error.slice(1).slice(0, -1))
    })
    .then(({ id }) => ({
      id,
    }))

export const cancelAppointment = (
  appointmentId,
  cancelledBy,
  cancelledByName,
  cancellationReason,
  deactivateCustomer,
  deactivationReason,
  deactivationTimeframe
) => {
  const body = {
    cancelledBy,
    cancelledByUserName: cancelledByName,
    cancellationReason,
  }

  if (deactivateCustomer !== undefined) {
    body.deactivateCustomer = deactivateCustomer
    body.deactivationReason = deactivationReason
    body.deactivationTimeframe = deactivationTimeframe
  }

  return fetch(
    `${settings.badaAppointmentApiUrl}/appointment/kratos/${appointmentId}?brand=${settings.brand}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: user?.access_token
          ? `Bearer ${user.access_token}`
          : undefined,
      },
      body: JSON.stringify(body),
    }
  ).then(async (r) => {
    if (r.ok) {
      return r.text()
    }

    const error = await r.text()
    throw new Error(error)
  })
}

export const updatePrepform = (
  appointmentId,
  {
    questions,
    willSendEmailUpdate,
    willBringKitchenImages,
    willBringKitchenSketchImages,
    connectionId,
    skipNotification,
  }
) =>
  fetch(
    `${settings.badaAppointmentApiUrl}/appointment/kratos/${appointmentId}/prepform?brand=${settings.brand}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: user?.access_token
          ? `Bearer ${user.access_token}`
          : undefined,
      },
      body: JSON.stringify({
        questions,
        sendEmailUpdate: willSendEmailUpdate,
        willBringCurrentKitchenImages: willBringKitchenImages,
        willBringKitchenScetchImages: willBringKitchenSketchImages,
        connectionId,
        skipNotification,
      }),
    }
  )
    .then(async (r) => {
      if (r.ok) {
        return r.json()
      }

      const error = await r.text()
      throw new Error(error)
    })
    .then(({ id }) => ({
      id,
    }))

export const updateAppointmentDetails = (
  appointmentId,
  {
    selectedDate,
    selectedTime,
    selectedEndTime,
    serviceId,
    serviceProviderId,
    updatedBy,
    updatedByName,
  }
) =>
  fetch(
    `${settings.badaAppointmentApiUrl}/appointment/kratos/${appointmentId}/details?brand=${settings.brand}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: user?.access_token
          ? `Bearer ${user.access_token}`
          : undefined,
      },
      body: JSON.stringify({
        selectedDate,
        selectedTime,
        selectedEndTime,
        serviceId,
        serviceProviderId,
        updatedBy,
        updatedByUserName: updatedByName,
      }),
    }
  ).then(async (r) => {
    if (r.ok) {
      return r.text()
    }

    const error = await r.text()
    throw new Error(error)
  })

export const amendAppointment = (
  appointmentId,
  {
    selectedDate,
    selectedTime,
    selectedEndTime,
    serviceId,
    serviceProviderId,
    updatedBy,
    updatedByName,
  }
) =>
  fetch(
    `${settings.badaAppointmentApiUrl}/appointment/kratos/${appointmentId}?brand=${settings.brand}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: user?.access_token
          ? `Bearer ${user.access_token}`
          : undefined,
      },
      body: JSON.stringify({
        selectedDate,
        selectedTime,
        selectedEndTime,
        serviceId,
        serviceProviderId,
        updatedBy,
        updatedByUserName: updatedByName,
      }),
    }
  ).then(async (r) => {
    if (r.ok) {
      return r.text()
    }

    const error = await r.text()
    throw new Error(error)
  })

export const getCalendar = async ({
  locationId,
  serviceId,
  fromDate,
  toDate,
  subsystem,
  serviceProviderId,
  style,
  timeFrame,
  serviceDuration,
  appointmentId,
  includeHidden,
}) => {
  const url = new URL(
    `${settings.badaAppointmentApiUrl}/calendar/simplybookme?brand=${settings.brand}`
  )
  url.searchParams.append('locationId', locationId)
  url.searchParams.append('serviceId', serviceId)
  url.searchParams.append('fromDate', fromDate)
  url.searchParams.append('toDate', toDate)
  url.searchParams.append('style', style)
  if (subsystem) {
    url.searchParams.append('subsystem', subsystem)
  }
  if (includeHidden) {
    url.searchParams.append('includeHidden', includeHidden)
  }
  if (serviceProviderId) {
    url.searchParams.append('serviceProviderId', serviceProviderId)
  }
  if (timeFrame) {
    url.searchParams.append('timeFrame', timeFrame)
  }
  if (serviceDuration) {
    url.searchParams.append('serviceDuration', serviceDuration)
  }
  if (appointmentId) {
    url.searchParams.append('appointmentId', appointmentId)
  }
  const response = await fetch(url.toString(), {
    headers: {
      Authorization: user?.access_token
        ? `Bearer ${user.access_token}`
        : undefined,
    },
  })
  if (response.ok) {
    const calendar = await response.json()
    return (
      calendar?.map((d) => ({
        closed: !d.from || !d.to || !d.slots?.length,
        day: d.date,
        from: d.from,
        to: d.to,
        intervals:
          d.slots?.map((s) => ({
            from: s.from,
            to: s.to,
            available: s.available,
            type: s.available ? 'available' : 'reserved',
          })) || [],
      })) || []
    )
  }
  const error = await response.text()
  throw new Error(error)
}

export {
  retrieveAddress,
  searchAddresses,
  geocodeAddress,
  serviceProviderByEmail,
  serviceProviderById,
  POSTCODE_ITEM_TYPE,
  ADDRESS_ITEM_TYPE,
}
